import React, { useEffect, useState } from "react";
import { Smartphone, BellRing, Download, Share, PlusSquare, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { api } from "../../lib/api";
import { pushSupported, isIOS, isStandalone, currentSub, enablePush, disablePush } from "../../lib/push";

const Step = ({ n, children }) => (
  <li className="flex items-start gap-2.5"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0e8c7f] text-[11px] font-bold text-white">{n}</span><span>{children}</span></li>
);

const InstallHelp = () => {
  const [prompt, setPrompt] = useState(window.__swInstall || null);
  useEffect(() => {
    const on = () => setPrompt(window.__swInstall);
    window.addEventListener("sw-installable", on);
    return () => window.removeEventListener("sw-installable", on);
  }, []);
  if (isStandalone()) return <p className="rounded-xl bg-[#ecfbf3] px-3 py-2 text-[13.5px] font-semibold text-[#0b6f45]" data-testid="app-installed-note">Installed. You're using the StripWise app.</p>;
  const install = async () => { prompt.prompt(); await prompt.userChoice; window.__swInstall = null; setPrompt(null); };
  return (
    <div className="space-y-3" data-testid="install-help">
      {prompt && <button onClick={install} className="sw-btn-primary !py-2 text-[14px]" data-testid="install-app-btn"><Download size={15} />Install app on this device</button>}
      {isIOS() ? (
        <div data-testid="ios-install-steps">
          <ol className="space-y-2.5 text-[14.5px] text-[#3d504d]">
            <Step n="1">Make sure this page is open in <b>Safari</b> (not Chrome or Facebook).</Step>
            <Step n="2">Tap the address bar at the <b>bottom</b> (<b>stripwisepays.com</b>) so the toolbar shows.</Step>
            <Step n="3">Tap <Share size={14} className="inline -mt-0.5" /> <b>Share</b> — or <b>⋯</b> then <Share size={14} className="inline -mt-0.5" /> <b>Share</b> on newer iPhones.</Step>
            <Step n="4">Scroll down, tap <PlusSquare size={14} className="inline -mt-0.5" /> <b>Add to Home Screen</b>, then <b>Add</b>.</Step>
            <Step n="5">Open the new <b>StripWise</b> icon on your home screen and log in.</Step>
          </ol>
          <div className="mt-4 flex flex-col items-center gap-1 rounded-2xl bg-[#e8f8f4] p-3 text-center text-[13px] font-semibold text-[#0b6f65]">
            Your Share button is at the bottom of the screen
            <span className="animate-bounce text-[26px] leading-none">↓</span>
          </div>
        </div>
      ) : !prompt && (
        <ol className="space-y-2 text-[13.5px] text-[#3d504d]" data-testid="android-install-steps">
          <Step n="1">Open <b>stripwisepays.com/admin</b> in <b>Chrome</b> on your phone.</Step>
          <Step n="2">Tap the <b>⋮</b> menu (top right) → <b>Install app</b> or <b>Add to Home screen</b>.</Step>
          <Step n="3">Open StripWise from your home screen and turn on notifications below.</Step>
        </ol>
      )}
    </div>
  );
};

const PushToggle = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSub().then((s) => setOn(!!s && Notification.permission === "granted")).catch(() => {}); }, []);
  const needsInstall = isIOS() && !isStandalone();
  const toggle = async (v) => {
    setBusy(true);
    try {
      if (v) { await enablePush(); setOn(true); toast.success("Notifications are on for this device"); }
      else { await disablePush(); setOn(false); toast.success("Notifications turned off for this device"); }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Could not turn on notifications"); }
    setBusy(false);
  };
  const test = async () => {
    try { await api.post("/admin/push/test"); toast.success("Test notification sent"); } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };
  if (!pushSupported() || needsInstall) {
    return <p className="rounded-xl bg-amber-50 px-3 py-2 text-[13px] text-amber-800" data-testid="push-unsupported">{needsInstall ? "On iPhone, add StripWise to your Home Screen first (steps above), then open it from there to turn on notifications." : "This browser doesn't support push notifications. Use Chrome on Android, or Safari on iPhone after installing the app."}</p>;
  }
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3"><Switch checked={on} disabled={busy} onCheckedChange={toggle} data-testid="push-enable-switch" /><span className="text-[14px] font-medium">Notifications on this device</span>{busy && <Loader2 size={15} className="animate-spin text-[#0e8c7f]" />}</label>
      {on && <button onClick={test} className="sw-btn-outline !py-2 text-[14px]" data-testid="push-test-btn"><Send size={15} />Send test notification</button>}
    </div>
  );
};

export const PhoneAppCard = () => (
  <div className="space-y-4 rounded-2xl border border-[#e3f0ed] bg-white p-6" data-testid="phone-app-card">
    <div className="flex items-center gap-2"><Smartphone size={18} className="text-[#0e8c7f]" /><h3 className="text-[18px] font-bold">Phone app & notifications</h3></div>
    <p className="text-[14px] text-[#5b6d6a]">Install StripWise on your phone's home screen and get a ringing notification for every new chat, text and quote, even when the app is closed. Chats claimed by an agent only notify that agent.</p>
    <InstallHelp />
    <div className="flex items-center gap-2 border-t border-[#eef5f3] pt-4"><BellRing size={16} className="text-[#0e8c7f]" /><span className="text-[14.5px] font-semibold">Push notifications</span></div>
    <PushToggle />
  </div>
);
